import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getHomeRouteByRole } from "../../utils/rutas";
import {
  ROLES_GESTION_ANUNCIOS,
  STAFF_ROLES,
  PLATFORM_DIRECTOR_ROLES,
  ROLES_CREAR_CURSO,
  COURSE_ADMIN_ASSIGN_ROLES,
} from "../../utils/roles";
import { API_URL } from "../../config";
import { extractMongoObjectIdHex } from "../../utils/helpers";
import { FALLBACK_IMAGE_SRC, handleImageError } from "../../utils/placeholderImage";
import {
  showConfigurarTestsButton,
  showInstructorGestionarCursoButton,
} from "./gestionCursosSidebarRules";
import { duplicarCursoAdmin } from "./gestionCursosDuplicarCurso";
import {
  buildVistaCursoAdminPath,
  VISTA_CURSO_RETURN_GESTION,
} from "../../utils/vistaCursosAdminReturn";
import ResponsiveActionSidebar from "../../components/ResponsiveActionSidebar";
import "./GestionCursos.css";

const ALLOWED_ROLES = new Set(STAFF_ROLES);

function etiquetaEstadoCatalogo(curso) {
  const ep = curso?.estadoPublicacion;
  if (!ep || typeof ep.clave !== "string") return "";
  switch (ep.clave) {
    case "borrador_catalogo":
      return "Oculto del catálogo";
    case "cambios_pendientes":
      return "Cambios sin publicar";
    case "publicado":
      return "Publicado en catálogo";
    default:
      return ep.clave;
  }
}

function etiquetaEstadoActividad(curso) {
  return curso?.activo === false ? "Curso inactivo" : "Curso activo";
}

function formatCourseEndDate(value) {
  if (!value) return "Sin fecha de finalización";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha de finalización no válida";
  return `Finaliza: ${date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}`;
}

function normalizeCursosResponse(datos) {
  if (Array.isArray(datos)) return datos;
  if (Array.isArray(datos?.cursos)) return datos.cursos;
  return [];
}

function getPublishPermissionTitle(canPublishCatalogo) {
  if (canPublishCatalogo) return undefined;
  return "Requiere permiso puedePublicar";
}

const GestionCursos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rol, setRol] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [puedeModificar, setPuedeModificar] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data && (data.ok || data.usuario)) {
          setRol(data.usuario?.rol || null);
          setPuedePublicar(data.usuario?.puedePublicar === true);
          setPuedeActivar(data.usuario?.permisos?.puedeActivar === true);
          setPuedeDuplicar(data.usuario?.permisos?.puedeDuplicar === true);
          setPuedeModificar(data.usuario?.permisos?.puedeModificar === true);
          setPuedeBorrarCursos(data.usuario?.permisos?.puedeBorrarCursos === true);
        }
      })
      .catch((err) => {
        console.error("Error obteniendo rol:", err);
        setRol(null);
      })
      .finally(() => setRoleLoaded(true));
  }, []);

  useEffect(() => {
    const st = location.state;
    if (st && typeof st === "object" && typeof st.gcNotice === "string" && st.gcNotice.trim()) {
      setMensaje(st.gcNotice.trim());
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const [cursos, setCursos] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [puedePublicar, setPuedePublicar] = useState(false);
  const [puedeActivar, setPuedeActivar] = useState(false);
  const [puedeDuplicar, setPuedeDuplicar] = useState(false);
  const [puedeBorrarCursos, setPuedeBorrarCursos] = useState(false);
  const [isCatalogActionInFlight, setIsCatalogActionInFlight] = useState(false);
  const [isActivityActionInFlight, setIsActivityActionInFlight] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  useEffect(() => {
    if (!roleLoaded) return;
    if (!ALLOWED_ROLES.has(rol) && !puedeModificar) {
      navigate(getHomeRouteByRole(rol || "alumno"), { replace: true });
      return;
    }
    obtenerCursos();
  }, [roleLoaded, rol, puedeModificar, navigate]);

  const obtenerCursos = async () => {
    try {
      const respuesta = await fetch(`${API_URL}/cursos`, {
      method: "GET",
      credentials: "include",
        headers: { 
          "Content-Type": "application/json",
        },
      });

      if (!respuesta.ok) {
        throw new Error("Error al obtener cursos");
      }
      
      const datos = await respuesta.json();
      const lista = normalizeCursosResponse(datos);
      setCursos(lista);
      setSelectedCurso((prev) => {
        if (prev?._id == null) return prev;
        const prevHex = extractMongoObjectIdHex(prev._id);
        if (!prevHex) return prev;
        const actualizado = lista.find((c) => extractMongoObjectIdHex(c._id) === prevHex);
        return actualizado || prev;
      });
    } catch (error) {
      console.error("obtener cursos:", error);
      setMensaje("Error de conexión al obtener cursos");
    }
  };

  const postCursoAction = async ({
    endpoint,
    inFlightSetter,
    successMessage,
    fallbackErrorMessage,
    confirmMessage,
    buildSelectedCursoPatch,
  }) => {
    if (!selectedCurso) {
      setMensaje("⚠️ Selecciona un curso.");
      return;
    }
    const cursoId = extractMongoObjectIdHex(selectedCurso._id);
    if (!cursoId) {
      setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
      return;
    }

    if (confirmMessage && !globalThis.confirm(confirmMessage)) {
      return;
    }

    inFlightSetter(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/cursos/${cursoId}/${endpoint}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMensaje(
          typeof data?.error === "string" && data.error.trim()
            ? data.error
            : fallbackErrorMessage,
        );
        return;
      }

      setMensaje(successMessage);
      await obtenerCursos();
      setSelectedCurso((prev) => {
        if (!prev || extractMongoObjectIdHex(prev._id) !== cursoId) return prev;
        const patch = buildSelectedCursoPatch?.(data) || null;
        return patch ? { ...prev, ...patch } : prev;
      });
    } catch (e) {
      console.error("acción de curso:", e);
      setMensaje("Error de conexión al actualizar el curso.");
    } finally {
      inFlightSetter(false);
    }
  };

  const handlePublicarCatalogo = () =>
    postCursoAction({
      endpoint: "publicar",
      inFlightSetter: setIsCatalogActionInFlight,
      successMessage: "Curso publicado en el catálogo.",
      fallbackErrorMessage: "No se pudo publicar el curso en el catálogo.",
      buildSelectedCursoPatch: (data) =>
        data?.estadoPublicacion ? { estadoPublicacion: data.estadoPublicacion } : null,
    });

  const handleDespublicarCatalogo = () =>
    postCursoAction({
      endpoint: "despublicar-catalogo",
      inFlightSetter: setIsCatalogActionInFlight,
      successMessage: "Curso oculto del catálogo.",
      fallbackErrorMessage: "No se pudo ocultar el curso del catálogo.",
      buildSelectedCursoPatch: (data) =>
        data?.estadoPublicacion ? { estadoPublicacion: data.estadoPublicacion } : null,
    });

  const handleSetCursoActivo = async (nextActivo) => {
    const tituloCurso = selectedCurso?.titulo || selectedCurso?.nombre || "este curso";
    const confirmText = nextActivo
      ? `¿Reactivar "${tituloCurso}" para que vuelva a estar disponible en los flujos activos del alumno?`
      : `¿Desactivar "${tituloCurso}"? El alumno no podrá usarlo en selector, comunicaciones ni compra.`;

    return postCursoAction({
      endpoint: nextActivo ? "reactivar" : "desactivar",
      inFlightSetter: setIsActivityActionInFlight,
      successMessage: nextActivo ? "Curso reactivado correctamente." : "Curso desactivado correctamente.",
      fallbackErrorMessage: "No se pudo actualizar el estado de actividad del curso.",
      confirmMessage: confirmText,
      buildSelectedCursoPatch: (data) => {
        const patch = {};
        if (typeof data?.curso?.activo === "boolean") {
          patch.activo = data.curso.activo;
        }
        if (data?.estadoPublicacion) {
          patch.estadoPublicacion = data.estadoPublicacion;
        }
        return Object.keys(patch).length > 0 ? patch : null;
      },
    });
  };

  const handleSelectCurso = (curso) => {
    setSelectedCurso(curso);
  };

const handleDeleteCurso = async () => {
  if (!selectedCurso) return;

  const deleteId = extractMongoObjectIdHex(selectedCurso._id);
  if (!deleteId) {
    setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
    return;
  }

  try {
    const url = `${API_URL}/cursos/${deleteId}`;
    const respuesta = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!respuesta.ok) {
      throw new Error(`Error al eliminar el curso. Código: ${respuesta.status}`);
    }

    setMensaje("Curso eliminado correctamente.");
    setShowConfirmDelete(false);
    setSelectedCurso(null);
    obtenerCursos(); // 🔥 Actualiza la lista después de eliminar
  } catch (error) {
    console.error("Error al eliminar el curso:", error);
    setMensaje("Error al eliminar el curso. Revisa la consola.");
  }
};

const handleVisualizarCurso = () => {
  if (!selectedCurso) {
    setMensaje("⚠️ Selecciona un curso para visualizarlo.");
    return;
  }
  const cursoId = extractMongoObjectIdHex(selectedCurso._id);
  if (!cursoId) {
    setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
    return;
  }
  navigate(buildVistaCursoAdminPath(cursoId, VISTA_CURSO_RETURN_GESTION));
};

  const handleDuplicarCurso = async () => {
    if (!selectedCurso || isDuplicating) {
      if (!selectedCurso) setMensaje("⚠️ Selecciona un curso para duplicarlo.");
      return;
    }
    const tituloOrigen = selectedCurso.titulo || selectedCurso.nombre || "curso";
    if (
      !globalThis.confirm(
        `¿Duplicar "${tituloOrigen}" como borrador? Se copiará la estructura y contenidos.`
      )
    ) {
      return;
    }
    setIsDuplicating(true);
    try {
      const result = await duplicarCursoAdmin({ cursoId: selectedCurso._id });
      if (!result.ok) {
        setMensaje(`⚠️ ${result.error}`);
        return;
      }
      await obtenerCursos();
      const nuevoId = extractMongoObjectIdHex(result.curso?._id);
      setMensaje("Curso duplicado correctamente.");
      if (nuevoId) {
        navigate(`/admin/editar-curso/${nuevoId}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al duplicar el curso.";
      setMensaje(`⚠️ ${msg}`);
    } finally {
      setIsDuplicating(false);
    }
  };

  const cursosFiltrados = cursos.filter((curso) =>
    (curso.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || 
     curso.titulo?.toLowerCase().includes(busqueda.toLowerCase()))
  );
  
  const canManageCourses = roleLoaded && (PLATFORM_DIRECTOR_ROLES.includes(rol) || puedeModificar);
  const canCreateCourse =
    roleLoaded && (ROLES_CREAR_CURSO.includes(rol) || puedePublicar);
  const canDeleteCourses =
    roleLoaded && (rol === "director" || puedeBorrarCursos);
  const canOpenAnuncios = roleLoaded && ROLES_GESTION_ANUNCIOS.includes(rol);
  const canToggleCatalogoRole =
    roleLoaded && (COURSE_ADMIN_ASSIGN_ROLES.includes(rol) || puedePublicar);
  const canPublishCatalogo = canToggleCatalogoRole && puedePublicar;
  const canActivateCourse = roleLoaded && puedeActivar;
  const canDuplicateCourse = roleLoaded && puedeDuplicar;

  const handleVolver = () => {
    navigate(getHomeRouteByRole(rol || "alumno"), { replace: true });
  };

  const catalogoClave = selectedCurso?.estadoPublicacion?.clave;
  const visibleEnCatalogo = selectedCurso?.estadoPublicacion?.visibleEnCatalogo === true;
  const showPublicarCatalogoBtn =
    canToggleCatalogoRole && selectedCurso && catalogoClave !== "publicado";
  const showOcultarCatalogoBtn = canToggleCatalogoRole && selectedCurso && visibleEnCatalogo;
  const showDeactivateCourseBtn =
    canActivateCourse && selectedCurso?.activo !== false;
  const showReactivateCourseBtn =
    canActivateCourse && selectedCurso?.activo === false;
  const publishPermissionTitle = getPublishPermissionTitle(canPublishCatalogo);

  return (
    <div className="gc-container">
      
      <div className="gc-main-container">
        <div className="gc-content">
          <h1>Gestión de Cursos</h1>
          {roleLoaded && rol === "instructor" && (
            <p className="gc-instructor-hint" style={{ marginBottom: 16, padding: 12, background: "#e8f5e9", borderRadius: 8, fontSize: "0.95rem", lineHeight: 1.5 }}>
              Al seleccionar un curso, pulsa <strong>Gestionar curso</strong> en la derecha para abrir el editor (datos,{" "}
              <strong>documentos</strong>, temario y tests). <strong>Visualizar curso</strong> sirve para previsualizar sin guardar.
            </p>
          )}
          <input
            type="text"
            className="form-control gc-search"
            placeholder="Buscar curso por nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <div className="gc-user-list-container">
            <h4>Lista de Cursos</h4>
            {cursosFiltrados.length === 0 ? (
              <p>No hay cursos registrados</p>
            ) : (
              <ul className="gc-user-list">
              {cursosFiltrados.map((curso) => (
                <li key={curso._id}>
                  <button
                    type="button"
                    className={`gc-user-item ${selectedCurso && selectedCurso._id === curso._id ? "selected" : ""}`}
                    onClick={() => handleSelectCurso(curso)}
                  >
                    <span className="gc-curso-image-frame">
                      <img
                        src={curso.imagen || FALLBACK_IMAGE_SRC}
                        alt=""
                        className="gc-curso-image ui-integrated-image"
                        onError={handleImageError}
                      />
                    </span>
                    <span className="gc-curso-titulo">{curso.titulo || curso.nombre || "(Sin título)"}</span>
                    <span className="gc-curso-fecha-cierre">{formatCourseEndDate(curso.fechaCierre)}</span>
                    <span className={`gc-estado-badge ${curso.activo === false ? "gc-estado-inactivo" : "gc-estado-activo"}`}>
                      {etiquetaEstadoActividad(curso)}
                    </span>
                    {curso.estadoPublicacion && (
                      <span className={`gc-estado-badge gc-estado-${curso.estadoPublicacion.clave || "unknown"}`}>
                        {etiquetaEstadoCatalogo(curso)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            )}
          </div>
          {mensaje && <p className="gc-message">{mensaje}</p>}
        </div>

        {/* sidebar */}
        <ResponsiveActionSidebar
          id="gc-actions-sidebar"
          className="gc-sidebar"
          openClassName="gc-sidebar--open"
          toggleClassName="gc-sidebar-toggle"
          label="Menú"
          ariaLabel="Acciones de gestión de cursos"
        >
          <h4>Acciones</h4>
          <ul className="gc-sidebar-links">
            {canCreateCourse && (
            <li>
              <Link
                to="/CrearCurso"
                state={{ startFresh: true }}
                className="regusu-sidebar-button"
                title="Al guardar el borrador, pasarás directamente a la gestión de ese curso."
              >
                Crear curso
              </Link>
            </li>
            )}
            {canOpenAnuncios && (
            <li>
              <Link to="/GestionAnuncios" className="regusu-sidebar-button">
                Comunicados
              </Link>
            </li>
            )}
            {canManageCourses && (
            <li>
                <button
                  type="button"
                  className="gc-sidebar-button"
                  onClick={() => {
                    if (!selectedCurso) return setMensaje("⚠️ Selecciona un curso.");
                    const cursoId = extractMongoObjectIdHex(selectedCurso._id);
                    if (!cursoId) return setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
                    if (globalThis.confirm(`¿Gestionar el curso "${selectedCurso.titulo || selectedCurso.nombre}"?`)) {
                      navigate(`/admin/editar-curso/${cursoId}`);
                    }
                  }}
                  disabled={!selectedCurso}
                >
                  Gestionar Curso
                </button>
             </li>
            )}

            <li>
                <button
                  type="button"
                  className="gc-sidebar-button"
                  onClick={handleVisualizarCurso}
                  disabled={!selectedCurso}
                >
                  Visualizar Curso
                </button>
              </li>

            {canDuplicateCourse && (
            <li>
              <button
                type="button"
                className="gc-sidebar-button"
                onClick={handleDuplicarCurso}
                disabled={!selectedCurso || isDuplicating}
              >
                {isDuplicating ? "Duplicando…" : "Duplicar curso"}
              </button>
            </li>
            )}

            {showPublicarCatalogoBtn && (
            <li>
              <button
                type="button"
                className="gc-sidebar-button"
                onClick={handlePublicarCatalogo}
                disabled={!selectedCurso || isCatalogActionInFlight || !canPublishCatalogo}
                title={publishPermissionTitle}
              >
                Publicar en catálogo
              </button>
            </li>
            )}
            {showOcultarCatalogoBtn && (
            <li>
              <button
                type="button"
                className="gc-sidebar-button"
                onClick={handleDespublicarCatalogo}
                disabled={!selectedCurso || isCatalogActionInFlight || !canPublishCatalogo}
                title={publishPermissionTitle}
              >
                Ocultar del catálogo
              </button>
            </li>
            )}

            {showDeactivateCourseBtn && (
            <li>
              <button
                type="button"
                className="gc-sidebar-button gc-sidebar-button--danger"
                onClick={() => handleSetCursoActivo(false)}
                disabled={!selectedCurso || isActivityActionInFlight}
              >
                Desactivar curso
              </button>
            </li>
            )}

            {showReactivateCourseBtn && (
            <li>
              <button
                type="button"
                className="gc-sidebar-button"
                onClick={() => handleSetCursoActivo(true)}
                disabled={!selectedCurso || isActivityActionInFlight}
              >
                Reactivar curso
              </button>
            </li>
            )}

            {showInstructorGestionarCursoButton(rol, roleLoaded) && (
            <li>
                <button
                  type="button"
                  className="gc-sidebar-button"
                  onClick={() => {
                    if (!selectedCurso) {
                      setMensaje("⚠️ Selecciona un curso.");
                      return;
                    }
                    const cursoId = extractMongoObjectIdHex(selectedCurso._id);
                    if (!cursoId) {
                      setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
                      return;
                    }
                    navigate(`/admin/editar-curso/${cursoId}`);
                  }}
                  disabled={!selectedCurso}
                >
                  Gestionar curso
                </button>
            </li>
            )}
            {showConfigurarTestsButton(rol, roleLoaded, puedeModificar) && (
            <li>
                <button
                  type="button"
                  className="gc-sidebar-button"
                  onClick={() => {
                    if (!selectedCurso) {
                      setMensaje("⚠️ Selecciona un curso.");
                      return;
                    }
                    const cursoId = extractMongoObjectIdHex(selectedCurso._id);
                    if (!cursoId) {
                      setMensaje("⚠️ El curso seleccionado no tiene identificador válido.");
                      return;
                    }
                    navigate(`/admin/curso/${cursoId}/tests-config`);
                  }}
                  disabled={!selectedCurso}
                >
                  Configurar tests
                </button>
            </li>
            )}

            {canDeleteCourses && (
            <li>
              <button
                className="gc-sidebar-button"
                onClick={() => setShowConfirmDelete(true)}
                disabled={!selectedCurso}
              >
                Borrar Curso
              </button>
            </li>
            )}
            <li>
              <button className="mi-sidebar-button staff-sidebar-exit-button" onClick={handleVolver}>
                Salir
               </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>


   {/* Modal de Confirmación */}
   {showConfirmDelete && (
    <div className="gc-modal-overlay">
      <div className="gc-modal">
        <h5>¿Seguro que deseas eliminar el curso "{selectedCurso?.nombre || selectedCurso?.titulo}"?</h5>
        <p>Esta acción no se puede deshacer.</p>
        <div className="gc-modal-buttons">
          <button className="gc-button cancel" onClick={() => setShowConfirmDelete(false)}>Cancelar</button>
          <button className="gc-button delete" onClick={handleDeleteCurso}>Eliminar</button>
        </div>
      </div>
    </div>
  )}
</div>
);
};

export default GestionCursos;
